import { useDispatch, useSelector } from 'react-redux'
import {
  clearInput,
  clearReservationInfo,
  clearTodayConditionInputFieldsInfo,
  selectInput,
  selectReservationInfo,
  selectTodayConditionInputFieldsInfo,
} from '../common/store/slices/facilityReserveTodayConditionInput'
import { useCallback, useEffect, useState } from 'react'
import { useErrorHandle } from '../common/error/errorHandler'
import { showLoading } from '../common/store/slices/application'
import { executePutTodayConditionInput } from '../../dataAccess/webApi/dao/reservationsDao'
import { useHistory, useParams } from 'react-router-dom'
import { facilityReservationTodayConditionInputCompletionUrl } from '../common/constant/appUrl'
import { PostDecodeMstDto } from '../../dataAccess/webApi/dto/decodeMstDto'
import { getDecodeMstValue } from '../common/getDecodeMstValue'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { yesNo } from '../common/constant/classification'

interface UrlParams {
  reservationNo: string
}

export interface TodayConditionInputDecodeMst {
  napType: PostDecodeMstDto[]
  defecationTiming: PostDecodeMstDto[]
  defecationCondition: PostDecodeMstDto[]
  eatingMethod: PostDecodeMstDto[]
  amountOfFood: PostDecodeMstDto[]
  brestFeedingDetail: PostDecodeMstDto[]
  escortType: PostDecodeMstDto[]
  belongings: PostDecodeMstDto[]
}

const TODAY_CONDITION_INPUT_DECODE_MST_LIST = {
  /** お昼寝の種類 */
  napType: 'nap_type',
  // 排便タイミング
  defecationTiming: 'defecation_timing',
  // 便の状態
  defecationCondition: 'defecation_condition',
  // 栄養方法
  eatingMethod: 'eating_method',
  // 食事量
  amountOfFood: 'amount_of_food',
  // 栄養方法詳細
  brestFeedingDetail: 'brest_feeding_detail',
  // 送迎者種類
  escortType: 'escort_type',
  // 持ち物
  belongings: 'belongings',
}

export const useAction = () => {
  const history = useHistory()
  const { reservationNo } = useParams<UrlParams>()

  const reservationInfo = useSelector(selectReservationInfo)
  const todayConditionInputFieldsInfo = useSelector(selectTodayConditionInputFieldsInfo)
  const input = useSelector(selectInput)
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const { addOperationLog, attachAccessData } = useOperationLog()

  const [todayConditionInputDecodeMst, setTodayConditionInputDecodeMst] = useState<TodayConditionInputDecodeMst | null>(
    null
  )
  const [regResultCode, setRegResultCode] = useState<number>()

  // 初期化処理
  const initialize = useCallback(
    async () => {
      // 必要な情報をdecode_mstから取得する
      const getDecodeMstValues: TodayConditionInputDecodeMst = await getDecodeMstValue(
        TODAY_CONDITION_INPUT_DECODE_MST_LIST
      )
      if (getDecodeMstValues) {
        setTodayConditionInputDecodeMst(getDecodeMstValues)
      }
    },
    [reservationNo]
  )

  // 初回レンダリング時の処理
  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000194 })

    dispatch(showLoading(errorHandle(() => initialize())))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const decide = useCallback(() => {
    if (input) {
      dispatch(
        showLoading({
          process: errorHandle(async () => {
            // オペレーションログの追加
            if (input?.updateDateTime) {
              addOperationLog({ operationId: OperationId.OP_00000196 })
            } else {
              addOperationLog({ operationId: OperationId.OP_00000195 })
            }
            attachAccessData({
              accessData: [
                {
                  userIdRegFlag: yesNo.yes,
                  childId: reservationInfo?.childId,
                  usageDate: reservationInfo?.usageDate,
                  reservationNo,
                },
              ],
            })

            // 更新処理の実行
            const response = await executePutTodayConditionInput(reservationNo, input)
            if (response.resultCode) {
              // 登録失敗時
              setRegResultCode(response.resultCode)
            } else {
              // 登録成功時
              // reduxで管理している値をクリアして完了画面に遷移する
              dispatch(clearReservationInfo())
              dispatch(clearTodayConditionInputFieldsInfo())
              dispatch(clearInput())
              history.push(facilityReservationTodayConditionInputCompletionUrl.url(reservationNo))
            }
          }),
          isHiddenMain: false,
        })
      )
    }
  }, [input])

  return {
    todayConditionInputDecodeMst,
    reservationInfo,
    todayConditionInputFieldsInfo,
    input,
    regResultCode,
    decide,
  }
}

//------------------------------------------------------------------------------------------
